import { useState, useEffect, useCallback } from 'react';
import { Horizon, Btn, Mo, Bd, Dp, Label } from './shared';
import { GOLD, EMBER, MONO } from '../lib/constants';
import { GRADE_COLORS, Card, SL, fmtDate } from './InsightsShared';
import { RevealScreen } from './InsightsReveal';
import { AccountSelect, TimeframeSelect, LoadingScreen, NoBank, BudgetTargetsScreen } from './InsightsSubScreens';
import { FinancialProfileWizard } from './FinancialProfileWizard';
import { CheckpointModal } from './CheckpointModal';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ INSIGHTS HOME — shows latest summary, history, budget targets ═══ */
function InsightsHome({ history, budgetTargets, onNewAnalysis, onViewAnalysis, onManageBudgets, accounts, institution, onDisconnectBank, onChangeAccount, onPurgeHistory, needsReauth, onReconnect, debts }) {
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [editingNickname, setEditingNickname] = useState(null); // account_id
  const [nicknameValue, setNicknameValue] = useState('');
  const [nicknames, setNicknames] = useState({});

  // Load nicknames on mount
  useEffect(() => {
    const loadNicknames = async () => {
      try {
        const res = await fetch(`${API}/api/plaid/account-nicknames`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setNicknames(data.nicknames || {});
        }
      } catch (err) {
        console.error('Failed to load nicknames:', err);
      }
    };
    if (accounts.length > 0) {
      loadNicknames();
    }
  }, [accounts.length]);

  const handleEditNickname = (account) => {
    setEditingNickname(account.id);
    setNicknameValue(nicknames[account.id] || '');
  };

  const handleSaveNickname = async (accountId) => {
    try {
      await fetch(`${API}/api/plaid/account-nickname`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, nickname: nicknameValue }),
      });
      setNicknames(prev => ({ ...prev, [accountId]: nicknameValue || null }));
      setEditingNickname(null);
    } catch (err) {
      console.error('Failed to save nickname:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingNickname(null);
    setNicknameValue('');
  };


  // Filter out broken $0 analyses
  const validHistory = (history || []).filter(a => a.total_spending > 0);
  const latest = validHistory[0];
  const gradeColor = latest ? (GRADE_COLORS[latest.spending_grade] || 'var(--mute)') : 'var(--mute)';

  // Calculate Projected Freedom Date
  const calculateFreedomDate = () => {
    if (!debts || debts.length === 0) return null;
    
    const totalDebt = debts.reduce((sum, d) => sum + (d.balance || 0), 0);
    const monthlyPayment = debts.reduce((sum, d) => sum + (d.minimum_payment || 0), 0);
    
    if (totalDebt === 0 || monthlyPayment === 0) return null;
    
    // Simple calculation: total debt / monthly payment = months
    const monthsToFreedom = Math.ceil(totalDebt / monthlyPayment);
    const freedomDate = new Date();
    freedomDate.setMonth(freedomDate.getMonth() + monthsToFreedom);
    
    return {
      date: freedomDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      months: monthsToFreedom,
      totalDebt
    };
  };

  const freedomData = calculateFreedomDate();

  const doDisconnect = async () => {
    setDisconnecting(true);
    await onDisconnectBank();
    setDisconnecting(false);
    setShowDisconnectConfirm(false);
  };

  return (
    <div data-testid="insights-home" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        <Mo style={{ fontSize: 11, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 16 }}>SPENDING INSIGHTS</Mo>

        {/* ── Projected Freedom Date Hero Card ── */}
        {freedomData && (
          <Card testId="freedom-date-hero" style={{ 
            background: 'linear-gradient(135deg, rgba(232, 168, 73, 0.12) 0%, rgba(255, 215, 0, 0.08) 100%)',
            border: '1px solid rgba(232, 168, 73, 0.3)',
            textAlign: 'center',
            padding: 24,
            marginBottom: 16,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Gold glow effect */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '150%',
              height: '150%',
              background: 'radial-gradient(circle, rgba(232, 168, 73, 0.2) 0%, transparent 70%)',
              pointerEvents: 'none',
              filter: 'blur(40px)'
            }} />
            <Mo style={{ fontSize: 9, fontWeight: 700, color: GOLD, letterSpacing: 2, display: 'block', marginBottom: 12, position: 'relative' }}>PROJECTED FREEDOM DATE</Mo>
            <Dp style={{ 
              fontSize: 48, 
              fontWeight: 900, 
              color: GOLD,
              textShadow: '0 0 30px rgba(232, 168, 73, 0.6), 0 0 60px rgba(232, 168, 73, 0.3), 0 0 90px rgba(255, 215, 0, 0.2)',
              letterSpacing: '-0.02em',
              display: 'block',
              marginBottom: 8,
              position: 'relative'
            }}>
              {freedomData.date}
            </Dp>
            <Mo style={{ fontSize: 11, color: 'rgba(232, 168, 73, 0.8)', display: 'block', marginBottom: 4, position: 'relative' }}>
              {freedomData.months} months to debt freedom
            </Mo>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', position: 'relative' }}>
              ${freedomData.totalDebt.toLocaleString()} total debt
            </Mo>
          </Card>
        )}

        {/* ── Connected Bank Card ── */}
        {accounts.length > 0 && (
          <Card testId="connected-bank-card">
            <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 8 }}>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5 }}>CONNECTED BANK</Mo>
              <button onClick={() => setShowDisconnectConfirm(true)} data-testid="disconnect-btn" style={{
                background: 'none', border: 'none', color: EMBER, fontFamily: MONO, fontSize: 10, cursor: 'pointer', letterSpacing: 1,
              }}>DISCONNECT</button>
            </div>
            <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>{institution || 'Bank'}</Dp>
            {accounts.map(a => {
              const nickname = nicknames[a.id];
              const isEditing = editingNickname === a.id;
              return (
                <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--faint)' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="text"
                        value={nicknameValue}
                        onChange={(e) => setNicknameValue(e.target.value)}
                        placeholder="Enter nickname"
                        autoFocus
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          background: 'var(--surface)',
                          border: '1px solid var(--ghost)',
                          borderRadius: 6,
                          color: 'var(--light)',
                          fontFamily: MONO,
                          fontSize: 12,
                          outline: 'none',
                        }}
                      />
                      <button
                        onClick={() => handleSaveNickname(a.id)}
                        style={{
                          padding: '6px 10px',
                          background: GOLD,
                          border: 'none',
                          borderRadius: 6,
                          color: '#000',
                          fontFamily: MONO,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        SAVE
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          padding: '6px 10px',
                          background: 'transparent',
                          border: '1px solid var(--ghost)',
                          borderRadius: 6,
                          color: 'var(--mute)',
                          fontFamily: MONO,
                          fontSize: 10,
                          cursor: 'pointer',
                        }}
                      >
                        CANCEL
                      </button>
                    </div>
                  ) : (
                    <div className="dettio-flex dettio-flex--between dettio-flex--center">
                      <div style={{ flex: 1 }}>
                        {nickname ? (
                          <>
                            <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm">
                              <Mo style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>{nickname}</Mo>
                              {(a.subtype || '').toLowerCase() === 'checking' && (
                                <Mo style={{ fontSize: 8, fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.15)', padding: '2px 6px', borderRadius: 4 }}>CHECKING</Mo>
                              )}
                            </div>
                            <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                              {a.name} ····{a.mask || '****'}
                            </Mo>
                          </>
                        ) : (
                          <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm">
                            <Mo style={{ fontSize: 12, color: 'var(--light)' }}>{a.name}</Mo>
                            {(a.subtype || '').toLowerCase() === 'checking' && (
                              <Mo style={{ fontSize: 8, fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.15)', padding: '2px 6px', borderRadius: 4 }}>CHECKING</Mo>
                            )}
                          </div>
                        )}
                        {!nickname && (
                          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                            ····{a.mask || '****'}
                          </Mo>
                        )}
                      </div>
                      <button
                        onClick={() => handleEditNickname(a)}
                        data-testid={`edit-nickname-${a.id}`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Edit nickname"
                      >
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2" strokeLinecap="round">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        )}

        {/* ── Re-auth Banner ── */}
        {needsReauth && (
          <Card testId="reauth-banner" style={{ background: `${EMBER}08`, border: `1px solid ${EMBER}25` }}>
            <div className="dettio-flex dettio-flex--center dettio-flex--gap-md">
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={EMBER} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <div style={{ flex: 1 }}>
                <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)', display: 'block' }}>Bank connection needs refresh</Bd>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                  Using cached data. Reconnect for live balances & transactions.
                </Mo>
              </div>
            </div>
            <button onClick={onReconnect} data-testid="reconnect-bank-btn" style={{
              width: '100%', marginTop: 12, padding: '10px', background: 'transparent',
              border: `1px solid ${EMBER}40`, color: EMBER, fontFamily: MONO, fontSize: 10,
              fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5, borderRadius: 8,
            }}>RECONNECT BANK</button>
          </Card>
        )}

        {/* ── Latest Analysis Summary ── */}
        {latest ? (
          <>
            <Card testId="latest-analysis-summary" style={{ background: `${GOLD}06`, border: `1px solid ${GOLD}20` }}>
              <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 16 }}>
                <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5 }}>LATEST ANALYSIS</Mo>
                <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>{fmtDate(latest.created_at)}</Mo>
              </div>
              <div className="dettio-flex dettio-flex--gap-lg" style={{ marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>TOTAL SPENT</Mo>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)' }}>${(latest.total_spending || 0).toLocaleString()}</Dp>
                </div>
                <div style={{ flex: 1, textAlign: 'right' }}>
                  <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>TOTAL INCOME</Mo>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>${(latest.total_income || 0).toLocaleString()}</Dp>
                </div>
              </div>
              <div className="dettio-flex dettio-flex--gap-md" style={{ marginBottom: 14 }}>
                <div className="dettio-stat-box" style={{ background: `${gradeColor}12`, border: `1px solid ${gradeColor}30` }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 2 }}>GRADE</Mo>
                  <Dp data-testid="spending-grade" style={{ fontSize: 22, fontWeight: 800, color: gradeColor }}>{latest.spending_grade || '—'}</Dp>
                </div>
                <div className="dettio-stat-box">
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 2 }}>TRANSACTIONS</Mo>
                  <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)' }}>{latest.transaction_count || 0}</Dp>
                </div>
                <div className="dettio-stat-box">
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 2 }}>% OF INCOME</Mo>
                  <Dp style={{ fontSize: 20, fontWeight: 800, color: latest.income_pct > 85 ? EMBER : GOLD }}>{latest.income_pct || 0}%</Dp>
                </div>
              </div>
            </Card>
            {/* Large View Details Button */}
            <Btn data-testid="view-details-btn" onClick={() => onViewAnalysis(latest.id)} gold style={{ marginBottom: 12 }}>
              VIEW DETAILS
            </Btn>
          </>
        ) : (
          <Card testId="no-analysis-card" style={{ textAlign: 'center', padding: 24 }}>
            <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>No analysis yet</Dp>
            <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6 }}>
              Run your first spending analysis to see where your money goes.
            </Bd>
          </Card>
        )}

        {/* ── Run / History ── */}
        <Btn data-testid="new-analysis-btn" onClick={onNewAnalysis} gold style={{ marginBottom: 12 }}>
          {latest ? 'RUN NEW ANALYSIS' : 'START ANALYSIS'}
        </Btn>

        {/* ── Budget Targets ── */}
        {budgetTargets?.length > 0 && (
          <Card testId="budget-summary">
            <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 12 }}>
              <SL>BUDGET TARGETS</SL>
              <button onClick={onManageBudgets} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontFamily: MONO, fontSize: 9, cursor: 'pointer' }}>EDIT</button>
            </div>
            {budgetTargets.map((t, i) => {
              const pct = t.current_spent && t.target ? Math.round((t.current_spent / t.target) * 100) : 0;
              return (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div className="dettio-flex dettio-flex--between" style={{ marginBottom: 4 }}>
                    <Mo style={{ fontSize: 11, color: 'var(--light)' }}>{t.display_name || t.category}</Mo>
                    <Mo style={{ fontSize: 11, color: pct > 100 ? EMBER : 'var(--mute)' }}>${t.current_spent || 0}/${t.target}</Mo>
                  </div>
                  <div style={{ height: 3, background: 'var(--faint)', borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: pct > 100 ? EMBER : GOLD, borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
            <Btn onClick={onManageBudgets} secondary style={{ marginTop: 8 }}>MANAGE BUDGETS</Btn>
          </Card>
        )}

        {/* ── Past Analyses ── */}
        {validHistory.length > 1 && (
          <Card testId="past-analyses">
            <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 12 }}>
              <SL>PAST ANALYSES</SL>
              {onPurgeHistory && (
                <button onClick={onPurgeHistory} data-testid="purge-history-btn" style={{
                  background: 'none', border: 'none', color: 'var(--mute)', fontFamily: MONO, fontSize: 9, cursor: 'pointer',
                }}>CLEAR HISTORY</button>
              )}
            </div>
            {validHistory.slice(1, 4).map(a => (
              <div key={a.id} onClick={() => onViewAnalysis(a.id)} className="dettio-flex dettio-flex--between dettio-flex--center" style={{ padding: '12px 0', borderBottom: '1px solid var(--faint)', cursor: 'pointer' }}>
                <div>
                  <Bd style={{ fontSize: 13, color: 'var(--light)' }}>
                    {fmtDate(a.created_at)}
                  </Bd>
                  <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{a.months || 0}mo · {a.transaction_count || 0} txns</Mo>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)' }}>${(a.total_spending || 0).toLocaleString()}</Mo>
                  <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{a.income_pct || 0}% of income</Mo>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* ── Disconnect Confirmation Modal ── */}
      {showDisconnectConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28 }}>
          <div data-testid="disconnect-confirm-modal" style={{ background: 'var(--surface)', border: `1px solid ${EMBER}40`, borderRadius: 16, padding: 24, maxWidth: 400, width: '100%' }}>
            <Mo style={{ fontSize: 10, color: EMBER, letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>DISCONNECT BANK</Mo>
            <Bd style={{ fontSize: 14, color: 'var(--light)', display: 'block', lineHeight: 1.6, marginBottom: 8 }}>
              Remove <span style={{ fontWeight: 700 }}>{institution || 'this bank'}</span>?
            </Bd>
            <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6, marginBottom: 20 }}>
              This will remove all linked accounts. You can then connect your everyday checking account for better analysis.
            </Bd>
            <Btn data-testid="confirm-disconnect-btn" onClick={doDisconnect} disabled={disconnecting}
              style={{ marginBottom: 8, background: EMBER, color: '#fff' }}>
              {disconnecting ? 'DISCONNECTING...' : 'YES, DISCONNECT'}
            </Btn>
            <Btn onClick={() => setShowDisconnectConfirm(false)} secondary data-testid="cancel-disconnect-btn">CANCEL</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN EXPORT
   ═══════════════════════════════════════════════════════════════ */
export function InsightsScreen({ navigate, debts, user }) {
  const [phase, setPhase] = useState('loading');
  const [accounts, setAccounts] = useState([]);
  const [institution, setInstitution] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [budgetTargets, setBudgetTargets] = useState([]);
  const [creditWarning, setCreditWarning] = useState(null);
  const [primaryAccountId, setPrimaryAccountId] = useState(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [financialProfile, setFinancialProfile] = useState(null);
  const [profileChecked, setProfileChecked] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const [acctRes, hRes, bRes, prefRes, profileRes] = await Promise.all([
        fetch(`${API}/api/insights/accounts`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/insights/history`, { credentials: 'include' }).then(r => r.ok ? r.json() : { analyses: [] }),
        fetch(`${API}/api/insights/budget-targets`, { credentials: 'include' }).then(r => r.ok ? r.json() : { targets: [] }),
        fetch(`${API}/api/insights/primary-account`, { credentials: 'include' }).then(r => r.ok ? r.json() : { account_id: null }),
        fetch(`${API}/api/user/financial-profile`, { credentials: 'include' }).then(r => r.ok ? r.json() : { profile: null }),
      ]);

      setHistory(hRes.analyses || []);
      setBudgetTargets(bRes.targets || []);
      if (prefRes.account_id) setPrimaryAccountId(prefRes.account_id);
      setFinancialProfile(profileRes.profile);
      setProfileChecked(true);

      if (!acctRes.connected || !acctRes.accounts?.length) {
        setPhase('no-bank');
        return;
      }
      setAccounts(acctRes.accounts);
      setInstitution(acctRes.institution);
      setNeedsReauth(!!acctRes.needs_reauth);
      setPhase('home');
    } catch {
      setPhase('no-bank');
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const hasCheckingAccounts = accounts.some(a => {
    const sub = (a.subtype || '').toLowerCase();
    return sub === 'checking' || sub === 'savings';
  });

  // Check if user needs to complete financial profile
  const needsFinancialProfile = profileChecked && !financialProfile?.completed_at;

  // Handle starting a new analysis - show wizard if needed
  const handleNewAnalysis = () => {
    if (needsFinancialProfile) {
      setPhase('profile-wizard');
    } else {
      proceedToAccountSelect();
    }
  };

  // Proceed to account selection after profile is complete or skipped
  const proceedToAccountSelect = () => {
    if (primaryAccountId) {
      const prim = accounts.find(a => a.id === primaryAccountId);
      if (prim) {
        setSelectedAccount(prim);
        setPhase('select-time');
        return;
      }
    }
    setPhase('select-account');
  };

  // Handle profile wizard completion
  const handleProfileComplete = (profile) => {
    setFinancialProfile(profile);
    proceedToAccountSelect();
  };

  // Handle profile wizard skip
  const handleProfileSkip = () => {
    proceedToAccountSelect();
  };

  // Checkpoint state
  const [checkpoints, setCheckpoints] = useState([]);
  const [pendingAnalysis, setPendingAnalysis] = useState(null);

  // Check for checkpoints before running full analysis
  const checkForCheckpoints = async (accountId, months) => {
    setPhase('analyzing');
    setError(null);
    
    try {
      // First, check if we need checkpoints
      const checkRes = await fetch(`${API}/api/insights/scan-checkpoints`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, months }),
      });
      
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (checkData.needs_checkpoints && checkData.checkpoints?.length > 0) {
          // Show checkpoint modal
          setCheckpoints(checkData.checkpoints);
          setPendingAnalysis({ accountId, months });
          setPhase('checkpoints');
          return;
        }
      }
      
      // No checkpoints needed, run full analysis
      await runAnalysis(accountId, months);
    } catch (e) {
      console.error('Checkpoint check failed:', e);
      // Fall back to regular analysis
      await runAnalysis(accountId, months);
    }
  };

  // Handle checkpoint completion
  const handleCheckpointsComplete = async (answers) => {
    setCheckpoints([]);
    if (pendingAnalysis) {
      await runAnalysis(pendingAnalysis.accountId, pendingAnalysis.months);
      setPendingAnalysis(null);
    }
  };

  // Handle checkpoint skip
  const handleCheckpointsSkip = async () => {
    setCheckpoints([]);
    if (pendingAnalysis) {
      await runAnalysis(pendingAnalysis.accountId, pendingAnalysis.months);
      setPendingAnalysis(null);
    }
  };

  const runAnalysis = async (accountId, months) => {
    setPhase('analyzing');
    setError(null);
    try {
      const res = await fetch(`${API}/api/insights/analyze`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, months }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Analysis failed'); }
      const data = await res.json();
      setAnalysisData(data);

      try {
        const aiRes = await fetch(`${API}/api/insights/ai-analysis`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ spending_data: data }),
        });
        if (aiRes.ok) {
          const aiResult = await aiRes.json();
          setAiData(aiResult.analysis);
        }
      } catch {}

      if (accountId !== primaryAccountId) {
        setPrimaryAccountId(accountId);
        fetch(`${API}/api/insights/primary-account`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ account_id: accountId }),
        }).catch(() => {});
      }

      setPhase('reveal');
    } catch (e) {
      setError(e.message);
      setPhase('home');
    }
  };

  const goHome = () => {
    setPhase('home');
    setAnalysisData(null);
    setAiData(null);
    fetch(`${API}/api/insights/history`, { credentials: 'include' }).then(r => r.ok ? r.json() : { analyses: [] }).then(d => setHistory(d.analyses || []));
  };

  const handleAccountSelect = (a) => {
    const sub = (a.subtype || '').toLowerCase();
    const typ = (a.type || '').toLowerCase();
    const isCredit = sub === 'credit card' || typ === 'credit';
    if (isCredit) {
      setCreditWarning(a);
    } else {
      setSelectedAccount(a);
      setPhase('select-time');
    }
  };

  const handleDisconnectBank = async () => {
    try {
      const res = await fetch(`${API}/api/plaid/disconnect`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: null }),
      });
      if (res.ok) {
        setAccounts([]);
        setInstitution('');
        setPhase('no-bank');
      }
    } catch {}
  };

  const handlePurgeHistory = async () => {
    try {
      await fetch(`${API}/api/insights/history/purge`, {
        method: 'POST', credentials: 'include',
      });
      const hRes = await fetch(`${API}/api/insights/history`, { credentials: 'include' }).then(r => r.ok ? r.json() : { analyses: [] });
      setHistory(hRes.analyses || []);
    } catch {}
  };

  if (phase === 'loading') return <LoadingScreen stats={null} />;
  if (phase === 'no-bank') return <NoBank navigate={navigate} />;

  // Financial Profile Wizard
  if (phase === 'profile-wizard') return (
    <FinancialProfileWizard 
      onComplete={handleProfileComplete}
      onSkip={handleProfileSkip}
    />
  );

  const handleViewAnalysis = async (analysisId) => {
    setPhase('loading');
    setError(null);
    try {
      const res = await fetch(`${API}/api/insights/analysis/${analysisId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to load analysis');
      }
      const data = await res.json();
      setAnalysisData(data);
      setAiData(null); // Clear any previous AI data - we may regenerate if needed
      setPhase('reveal');
    } catch (e) {
      setError(e.message);
      setPhase('home');
    }
  };

  if (phase === 'home') return (
    <InsightsHome
      history={history}
      budgetTargets={budgetTargets}
      accounts={accounts}
      institution={institution}
      needsReauth={needsReauth}
      debts={debts}
      onDisconnectBank={handleDisconnectBank}
      onPurgeHistory={handlePurgeHistory}
      onReconnect={() => navigate('bank-manage')}
      onNewAnalysis={handleNewAnalysis}
      onViewAnalysis={handleViewAnalysis}
      onManageBudgets={() => setPhase('budget-targets')}
    />
  );

  if (phase === 'select-account') return (
    <div>
      <AccountSelect accounts={accounts} institution={institution} onSelect={handleAccountSelect} onBack={() => setPhase('home')} />
      {error && <Mo style={{ color: EMBER, fontSize: 11, display: 'block', textAlign: 'center', padding: '0 28px' }}>{error}</Mo>}
      {creditWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28 }}>
          <div data-testid="credit-card-warning" style={{ background: 'var(--surface)', border: `1px solid ${EMBER}40`, borderRadius: 16, padding: 24, maxWidth: 400, width: '100%' }}>
            <Mo style={{ fontSize: 10, color: EMBER, letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>HEADS UP</Mo>
            <Bd style={{ fontSize: 14, color: 'var(--light)', display: 'block', lineHeight: 1.6, marginBottom: 8 }}>
              <span style={{ fontWeight: 700 }}>{creditWarning.name}</span> is a credit card.
            </Bd>
            <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6, marginBottom: 20 }}>
              {hasCheckingAccounts
                ? 'For the most accurate analysis, select the checking account where your income arrives.'
                : 'For real spending insights, connect the bank account where your paycheck lands.'}
            </Bd>
            {hasCheckingAccounts ? (
              <Btn data-testid="pick-checking-btn" onClick={() => setCreditWarning(null)} gold style={{ marginBottom: 8 }}>
                PICK A CHECKING ACCOUNT INSTEAD
              </Btn>
            ) : (
              <Btn data-testid="connect-checking-btn" onClick={() => { setCreditWarning(null); navigate('bank-manage'); }} gold style={{ marginBottom: 8 }}>
                CONNECT YOUR CHECKING ACCOUNT
              </Btn>
            )}
            <Btn data-testid="use-credit-anyway-btn" onClick={() => { setSelectedAccount(creditWarning); setCreditWarning(null); setPhase('select-time'); }} secondary>
              USE CREDIT CARD ANYWAY
            </Btn>
          </div>
        </div>
      )}
    </div>
  );

  if (phase === 'select-time') return (
    <TimeframeSelect account={selectedAccount} onSelect={(m) => checkForCheckpoints(selectedAccount.id, m)} onBack={() => setPhase('select-account')} />
  );

  if (phase === 'checkpoints' && checkpoints.length > 0) return (
    <CheckpointModal
      checkpoints={checkpoints}
      onComplete={handleCheckpointsComplete}
      onSkip={handleCheckpointsSkip}
    />
  );

  if (phase === 'analyzing') return <LoadingScreen stats={analysisData} />;

  // Re-analyze handler for RevealScreen
  const handleReAnalyze = async () => {
    // Use selectedAccount if available, otherwise fall back to account_id from the analysis data
    const accountId = selectedAccount?.id || analysisData?.account_id;
    if (!accountId) return;
    const months = analysisData?.months || 3;
    await runAnalysis(accountId, months);
  };

  if (phase === 'reveal' && analysisData) return (
    <RevealScreen
      data={analysisData} aiData={aiData} debts={debts}
      onBack={goHome}
      onSetBudgets={() => setPhase('budget-targets')}
      onReAnalyze={handleReAnalyze}
    />
  );

  if (phase === 'budget-targets') return (
    <BudgetTargetsScreen
      categories={analysisData?.categories || []}
      targets={budgetTargets}
      onBack={() => setPhase(analysisData ? 'reveal' : 'home')}
      onSave={(t) => { setBudgetTargets(t); setPhase(analysisData ? 'reveal' : 'home'); }}
    />
  );

  return <LoadingScreen stats={null} />;
}
